"use client"

import { useChatPanel, ChatPreset, type ChatSettings } from "@/contexts/ChatPanelContext"
import {
  Dialog,
  DialogTrigger,
  DialogPopup,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./Dialog"
import { Switch } from "./Switch"
import { Button } from "./Button"
import { cn } from "@/lib/utils"
import { Settings2, Sparkles, Eye, Check } from "lucide-react"
import { Tooltip, TooltipTrigger, TooltipPopup } from "./Tooltip"

const PRESETS: { key: ChatPreset; label: string; icon: typeof Sparkles; description: string }[] = [
  {
    key: "simple",
    label: "Simple",
    icon: Sparkles,
    description: "Clean chat — just messages and actions",
  },
  {
    key: "detailed",
    label: "Detailed",
    icon: Eye,
    description: "Timestamps, model info & context sent",
  },
]

interface SettingRowProps {
  label: string
  description: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}

function SettingRow({ label, description, checked, onCheckedChange }: SettingRowProps) {
  return (
    <label className="flex items-center justify-between gap-4 py-2.5 cursor-pointer group">
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <Switch
        checked={checked}
        onCheckedChange={onCheckedChange}
      />
    </label>
  )
}

const FONT_SIZE_OPTIONS: { value: ChatSettings["fontSize"]; label: string }[] = [
  { value: "small", label: "S" },
  { value: "medium", label: "M" },
  { value: "large", label: "L" },
]

export function ChatSettingsDialog() {
  const { settings, updateSettings, applyPreset } = useChatPanel()

  return (
    <Dialog>
      <Tooltip>
        <TooltipTrigger
          render={(props) => (
            <DialogTrigger
              render={(triggerProps) => (
                <Button
                  {...props}
                  {...triggerProps}
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={(e) => {
                    e.stopPropagation()
                    // Let base-ui handle the open
                    triggerProps.onClick?.(e)
                    props.onClick?.(e)
                  }}
                >
                  <Settings2 className="size-3.5" />
                </Button>
              )}
            />
          )}
        />
        <TooltipPopup>Chat settings</TooltipPopup>
      </Tooltip>

      <DialogPopup className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-md font-semibold">Chat Settings</DialogTitle>
          <DialogDescription>
            Choose a preset or customise what you see in the chat.
          </DialogDescription>
        </DialogHeader>

        {/* Preset cards */}
        <div className="grid grid-cols-2 gap-2 mt-4">
          {PRESETS.map(({ key, label, icon: Icon, description }) => {
            const isActive = settings.preset === key
            return (
              <button
                key={key}
                onClick={() => applyPreset(key)}
                className={cn(
                  "relative flex flex-col items-center gap-1.5 rounded-xl border p-3 text-center transition-all",
                  isActive ? "border-transparent ring-1 ring-white/25 text-white" : "border-border"
                )}
                style={isActive ? { background: "var(--color-recording)" } : undefined}
              >
                {isActive && (
                  <span className="absolute top-1.5 right-1.5">
                    <Check className="size-3 text-white" />
                  </span>
                )}
                <Icon className={cn("size-5", isActive ? "text-white" : "text-muted-foreground")} />
                <span className="text-xs font-semibold">{label}</span>
                <span className={cn("text-[10px] leading-tight", isActive ? "text-white/90" : "text-muted-foreground")}>{description}</span>
              </button>
            )
          })}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mt-5 mb-3">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Customise</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        {/* Transparency toggles */}
        <div className="space-y-0.5">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Transparency</p>

          <SettingRow
            label="Timestamps"
            description="Show when each message was sent"
            checked={settings.showTimestamps}
            onCheckedChange={(v) => updateSettings({ showTimestamps: v })}
          />
          <SettingRow
            label="Scope badge"
            description="Show the scope (Global / Deal / Document) on messages"
            checked={settings.showScopeBadge}
            onCheckedChange={(v) => updateSettings({ showScopeBadge: v })}
          />
          <SettingRow
            label="Context sent"
            description="Show which context items were included with each message"
            checked={settings.showContextSent}
            onCheckedChange={(v) => updateSettings({ showContextSent: v })}
          />
          <SettingRow
            label="Confidence"
            description="Show confidence indicators on responses"
            checked={settings.showConfidence}
            onCheckedChange={(v) => updateSettings({ showConfidence: v })}
          />
        </div>

        {/* Behaviour */}
        <div className="space-y-0.5 mt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Behaviour</p>

          <SettingRow
            label="Auto page context"
            description="Automatically attach the current page as context"
            checked={settings.autoPageContext}
            onCheckedChange={(v) => updateSettings({ autoPageContext: v })}
          />
        </div>

        {/* Font size */}
        <div className="mt-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Font size</p>
          <div className="flex gap-1.5">
            {FONT_SIZE_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => updateSettings({ fontSize: value })}
                className={cn(
                  "flex items-center justify-center h-8 w-10 rounded-lg border text-sm font-medium transition-all",
                  settings.fontSize === value
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border text-muted-foreground hover:border-primary/30"
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </DialogPopup>
    </Dialog>
  )
}
